import { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { parseChord } from '@/lib/pianoChords';

// Convert a chord's root pitch class (0-11, C=0) to a bass MIDI note.
// We aim for the E1..E2 range (~ 28-40) so it sounds like a bass. We anchor
// each root to the octave that keeps it >= 28 (E1) and <= 40 (E2).
function rootToMidi(rootPc) {
  // Start at 28 (E1) and pick the octave that is >= 28 and <= 39
  let midi = 24 + rootPc; // C1..B1
  while (midi < 28) midi += 12;
  while (midi > 40) midi -= 12;
  return midi;
}

function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/**
 * Preview a simple bass line under the timeline. Plays the ROOT of each chord
 * on beats 1 and 3 of every measure. Useful for creators to hear if the flow
 * works before publishing.
 *
 * Props:
 *   timeline: [{chord, beats}]
 *   bpm: number
 *   beatsPerMeasure: number (from time_signature)
 */
export default function BackingTrackPreview({ timeline, bpm = 90, beatsPerMeasure = 4 }) {
  const [playing, setPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(-1);
  const ctxRef = useRef(null);
  const timerRef = useRef(null);
  const stopRef = useRef(false);

  // Build a flat sequence of beat-slots so we can highlight progress.
  // Each entry: { chord, isDownbeat, beatInMeasure }
  const beatSequence = (() => {
    const out = [];
    let beatIndex = 0; // 0-based, resets each measure
    for (const row of (timeline || [])) {
      const b = Math.max(1, Number(row.beats) || 1);
      for (let i = 0; i < b; i++) {
        out.push({
          chord: row.chord,
          isDownbeat: (beatIndex % beatsPerMeasure) === 0,
          isBeat3: beatsPerMeasure === 4 && (beatIndex % beatsPerMeasure) === 2,
          globalBeat: beatIndex,
        });
        beatIndex += 1;
      }
    }
    return out;
  })();

  useEffect(() => () => {
    // Cleanup on unmount
    stopRef.current = true;
    if (timerRef.current) clearInterval(timerRef.current);
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch (_) { /* ignore */ }
      ctxRef.current = null;
    }
  }, []);

  const playBassHit = (chord, ctx, now) => {
    const parsed = parseChord(chord);
    if (!parsed) return;
    const midi = rootToMidi(parsed.rootIndex);
    const freq = midiToFreq(midi);

    const osc = ctx.createOscillator();
    osc.type = 'triangle'; // richer than sine, still bass-like
    osc.frequency.value = freq;

    // A subtle harmonic to give it a plucked feel
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = freq * 2;

    const gain = ctx.createGain();
    const attack = 0.008;
    const decay = 0.35;
    const peakGain = 0.28;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peakGain, now + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + attack + decay);

    const harmonicGain = ctx.createGain();
    harmonicGain.gain.setValueAtTime(0.0001, now);
    harmonicGain.gain.exponentialRampToValueAtTime(peakGain * 0.25, now + attack);
    harmonicGain.gain.exponentialRampToValueAtTime(0.0001, now + attack + decay * 0.6);

    osc.connect(gain).connect(ctx.destination);
    osc2.connect(harmonicGain).connect(ctx.destination);

    osc.start(now);
    osc2.start(now);
    osc.stop(now + attack + decay + 0.05);
    osc2.stop(now + attack + decay + 0.05);
  };

  const stop = () => {
    stopRef.current = true;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setPlaying(false);
    setCurrentBeat(-1);
  };

  const start = () => {
    if (beatSequence.length === 0) return;
    stopRef.current = false;
    if (!ctxRef.current || ctxRef.current.state === 'closed') {
      // eslint-disable-next-line no-undef
      const AC = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AC();
    }
    const ctx = ctxRef.current;
    if (ctx.state === 'suspended') ctx.resume();

    setPlaying(true);
    setCurrentBeat(0);

    const beatMs = 60000 / Math.max(30, bpm);
    let idx = 0;

    const tick = () => {
      if (stopRef.current) return;
      if (idx >= beatSequence.length) { stop(); return; }
      const slot = beatSequence[idx];
      // Play root only on beat 1 and beat 3 (for 4/4). For other TS, only downbeat.
      if (slot.isDownbeat || slot.isBeat3) {
        const now = ctx.currentTime + 0.01;
        playBassHit(slot.chord, ctx, now);
      }
      setCurrentBeat(idx);
      idx += 1;
    };

    tick(); // hit beat 1 immediately
    timerRef.current = setInterval(tick, beatMs);
  };

  const canPlay = beatSequence.length > 0;

  return (
    <div className="mt-3 rounded-lg border border-stone-800 bg-stone-900/60 p-3" data-testid="backing-track-preview">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={playing ? stop : start}
            disabled={!canPlay}
            data-testid="backing-track-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              !canPlay
                ? 'bg-stone-800 text-stone-500 cursor-not-allowed'
                : playing
                ? 'bg-amber-700 text-white hover:bg-amber-600'
                : 'bg-orange-700 text-white hover:bg-orange-600'
            }`}
          >
            {playing ? <Pause size={13} /> : <Play size={13} />}
            {playing ? 'Arrêter la basse' : 'Écouter la ligne de basse'}
          </button>
          <span className="text-[11px] text-stone-500">
            {beatSequence.length > 0
              ? `${beatSequence.length} temps · ${bpm} BPM · fondamentales sur les temps 1${beatsPerMeasure === 4 ? ' & 3' : ''}`
              : 'Ajoute au moins un accord pour prévisualiser'}
          </span>
        </div>
        {playing && currentBeat >= 0 && (
          <div className="text-[11px] font-mono text-orange-300">
            ♪ {beatSequence[currentBeat]?.chord} · temps {(currentBeat % beatsPerMeasure) + 1}/{beatsPerMeasure}
          </div>
        )}
      </div>
    </div>
  );
}
